import { useEffect, useState } from "react";
import { AuthTokenRetrieve, LoginButton } from "../Utility/AuthTokenRetrieve";
import { buildApiUrl } from "../Utility/api";
import "../Food/RecipeList.css";
import "./FoodDiary.css";
import { fetchDiaryMeals } from "./FoodDiaryShared";

const toLocalDateTimeInput = (value) => {
  if (!value) {
    return "";
  }

  const [datePart, timePart, meridiem] = value.split(" ");
  if (!datePart || !timePart || !meridiem) {
    return "";
  }

  const [month, day, year] = datePart.split("/");
  let [hours, minutes] = timePart.split(":");
  let parsedHours = Number(hours);
  if (meridiem === "PM" && parsedHours < 12) {
    parsedHours += 12;
  }
  if (meridiem === "AM" && parsedHours === 12) {
    parsedHours = 0;
  }

  return `${year}-${month}-${day}T${String(parsedHours).padStart(2, "0")}:${minutes}`;
};

const FoodDiaryMeals = () => {
  const [shouldShow, setShouldShow] = useState();
  const [meals, setMeals] = useState([]);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [editingMeal, setEditingMeal] = useState(false);
  const [mealForm, setMealForm] = useState({ eatenAt: "", mealType: "", notes: "" });
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemForm, setItemForm] = useState({ displayName: "", servingsEaten: "", notes: "" });
  const [editingIngredientId, setEditingIngredientId] = useState(null);
  const [ingredientForm, setIngredientForm] = useState({
    name: "",
    quantityValue: "",
    quantityText: "",
    unit: "",
    preparationNote: "",
  });
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  const accessToken = localStorage.getItem("lesterAccessToken") || "";

  useEffect(() => {
    if (!shouldShow || !accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchDiaryMeals(accessToken)
      .then((mealData) => {
        setMeals(mealData || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [shouldShow, accessToken]);

  const loadMealDetail = (mealId) => {
    setLoadingDetail(true);
    setStatusMessage("");
    fetch(buildApiUrl(`/food/diary/meals/${mealId}?accessToken=${encodeURIComponent(accessToken)}`), {
      cache: "no-cache",
    })
      .then((response) => response.json())
      .then((mealDetail) => {
        setSelectedMeal(mealDetail);
        setEditingMeal(false);
        setEditingItemId(null);
        setEditingIngredientId(null);
        setLoadingDetail(false);
      })
      .catch(() => {
        setLoadingDetail(false);
      });
  };

  const refreshMeals = () => {
    fetchDiaryMeals(accessToken).then((mealData) => {
      setMeals(mealData || []);
    });
  };

  const beginMealEdit = () => {
    if (!selectedMeal) {
      return;
    }
    setMealForm({
      eatenAt: toLocalDateTimeInput(selectedMeal.eatenAt),
      mealType: selectedMeal.mealType || "",
      notes: selectedMeal.notes || "",
    });
    setEditingMeal(true);
    setStatusMessage("");
  };

  const saveMeal = () => {
    fetch(buildApiUrl(`/food/diary/meals/${selectedMeal.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mealForm),
    })
      .then((response) => response.json())
      .then(() => {
        setEditingMeal(false);
        setStatusMessage("Meal updated.");
        refreshMeals();
        loadMealDetail(selectedMeal.id);
      });
  };

  const deleteMeal = () => {
    fetch(buildApiUrl(`/food/diary/meals/${selectedMeal.id}`), {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then(() => {
        setSelectedMeal(null);
        setStatusMessage("Meal deleted.");
        refreshMeals();
      });
  };

  const beginItemEdit = (item) => {
    setEditingItemId(item.id);
    setItemForm({
      displayName: item.displayName || "",
      servingsEaten: item.servingsEaten ?? "",
      notes: item.notes || "",
    });
    setStatusMessage("");
  };

  const saveItem = (itemId) => {
    fetch(buildApiUrl(`/food/diary/meal-items/${itemId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(itemForm),
    })
      .then((response) => response.json())
      .then(() => {
        setEditingItemId(null);
        setStatusMessage("Meal item updated.");
        refreshMeals();
        loadMealDetail(selectedMeal.id);
      });
  };

  const deleteItem = (itemId) => {
    fetch(buildApiUrl(`/food/diary/meal-items/${itemId}`), {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then(() => {
        setEditingItemId(null);
        setStatusMessage("Meal item deleted.");
        refreshMeals();
        loadMealDetail(selectedMeal.id);
      });
  };

  const beginIngredientEdit = (ingredient) => {
    setEditingIngredientId(ingredient.id);
    setIngredientForm({
      name: ingredient.name || "",
      quantityValue: ingredient.quantityValue ?? "",
      quantityText: ingredient.quantityText || "",
      unit: ingredient.unit || "",
      preparationNote: ingredient.preparationNote || "",
    });
    setStatusMessage("");
  };

  const saveIngredient = (ingredientId) => {
    fetch(buildApiUrl(`/food/diary/meal-item-ingredients/${ingredientId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ingredientForm),
    })
      .then((response) => response.json())
      .then(() => {
        setEditingIngredientId(null);
        setStatusMessage("Ingredient updated.");
        refreshMeals();
        loadMealDetail(selectedMeal.id);
      });
  };

  const deleteIngredient = (ingredientId) => {
    fetch(buildApiUrl(`/food/diary/meal-item-ingredients/${ingredientId}`), {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then(() => {
        setEditingIngredientId(null);
        setStatusMessage("Ingredient deleted.");
        refreshMeals();
        loadMealDetail(selectedMeal.id);
      });
  };

  if (!shouldShow) {
    return (
      <div className="searchBarDiv formViewWrapper FoodDiaryLogin">
        <h2>Food diary</h2>
        <p>Sign in to view private meal history.</p>
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  return (
    <div className="FoodDiaryPage">
      <div className="FoodDiaryGrid">
        <div className="shoppingListDiv FoodDiaryPanel">
          <h2 className="FoodDiaryTitle">Meal history</h2>
          <p className="FoodDiarySubtitle">Recent meals are shown in Central Time.</p>
          {loading ? (
            <p>Loading meals...</p>
          ) : meals.length ? (
            meals.map((meal) => (
              <button
                key={meal.id}
                className={`FoodDiaryHistoryButton ${selectedMeal?.id === meal.id ? "FoodDiaryHistoryButtonSelected" : ""}`}
                onClick={() => loadMealDetail(meal.id)}
              >
                <div>
                  <strong>{meal.mealType}</strong>
                  <div>{meal.eatenAt}</div>
                </div>
                <div className="FoodDiaryHistoryMeta">
                  <span>{meal.itemCount} items</span>
                  <span>{meal.ingredientCount} ingredients</span>
                </div>
              </button>
            ))
          ) : (
            <p>No meals logged yet.</p>
          )}
        </div>

        <div className="shoppingListDiv FoodDiaryPanel">
          <h2 className="FoodDiaryTitle">Meal detail</h2>
          {statusMessage ? <p className="FoodDiaryStatus">{statusMessage}</p> : null}
          {loadingDetail ? <p>Loading meal detail...</p> : null}
          {!loadingDetail && !selectedMeal ? <p>Select a meal to inspect the logged items and ingredients.</p> : null}
          {selectedMeal ? (
            <div className="FoodDiaryMealDetail">
              {editingMeal ? (
                <div className="FoodDiaryEditBlock">
                  <div className="FoodDiaryTwoColumn">
                    <div className="inputDiv">
                      <label>When eaten</label>
                      <input
                        type="datetime-local"
                        className="inputBox addInput"
                        value={mealForm.eatenAt}
                        onChange={(event) => setMealForm((currentValue) => ({ ...currentValue, eatenAt: event.target.value }))}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Meal type</label>
                      <input
                        className="inputBox addInput"
                        value={mealForm.mealType}
                        onChange={(event) => setMealForm((currentValue) => ({ ...currentValue, mealType: event.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="inputDiv">
                    <label>Notes</label>
                    <textarea
                      className="inputBox textAreaAdd FoodDiaryTextarea"
                      value={mealForm.notes}
                      onChange={(event) => setMealForm((currentValue) => ({ ...currentValue, notes: event.target.value }))}
                    />
                  </div>
                  <div className="FoodDiaryActionRow">
                    <button className="summaryButton" onClick={saveMeal}>Save</button>
                    <button className="summaryButton" onClick={() => setEditingMeal(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="FoodDiaryMealDetailHeader">
                    <strong>{selectedMeal.mealType}</strong>
                    <span>{selectedMeal.eatenAt}</span>
                  </div>
                  {selectedMeal.notes ? <p className="FoodDiaryMealNotes">{selectedMeal.notes}</p> : null}
                  <div className="FoodDiaryActionRow">
                    <button className="summaryButton" onClick={beginMealEdit}>Edit Meal</button>
                    <button className="summaryButton" onClick={deleteMeal}>Delete Meal</button>
                  </div>
                </>
              )}
              {selectedMeal.items.map((item) => (
                <div className="FoodDiaryMealItem" key={item.id}>
                  {editingItemId === item.id ? (
                    <div className="FoodDiaryEditBlock">
                      <div className="FoodDiaryMealItemHeader">
                        <strong>Edit item</strong>
                        <span>{item.sourceType}</span>
                      </div>
                      <div className="FoodDiaryTwoColumn">
                        <div className="inputDiv">
                          <label>Name</label>
                          <input
                            className="inputBox addInput"
                            value={itemForm.displayName}
                            onChange={(event) => setItemForm((currentValue) => ({ ...currentValue, displayName: event.target.value }))}
                          />
                        </div>
                        <div className="inputDiv">
                          <label>Servings</label>
                          <input
                            className="inputBox addInput"
                            value={itemForm.servingsEaten}
                            onChange={(event) => setItemForm((currentValue) => ({ ...currentValue, servingsEaten: event.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="inputDiv">
                        <label>Notes</label>
                        <input
                          className="inputBox addInput"
                          value={itemForm.notes}
                          onChange={(event) => setItemForm((currentValue) => ({ ...currentValue, notes: event.target.value }))}
                        />
                      </div>
                      <div className="FoodDiaryActionRow">
                        <button className="summaryButton" onClick={() => saveItem(item.id)}>Save</button>
                        <button className="summaryButton" onClick={() => setEditingItemId(null)}>Cancel</button>
                        <button className="summaryButton" onClick={() => deleteItem(item.id)}>Delete Item</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="FoodDiaryMealItemHeader">
                        <strong>{item.displayName}</strong>
                        <span>{item.sourceType}</span>
                      </div>
                      {item.servingsEaten ? <p>Servings: {item.servingsEaten}</p> : null}
                      {item.notes ? <p>{item.notes}</p> : null}
                      <div className="FoodDiaryActionRow">
                        <button className="summaryButton" onClick={() => beginItemEdit(item)}>Edit Item</button>
                        <button className="summaryButton" onClick={() => deleteItem(item.id)}>Delete Item</button>
                      </div>
                    </>
                  )}
                  <div className="FoodDiaryMealIngredientList">
                    {item.ingredients.map((ingredient, index) => (
                      <div className="FoodDiaryMealIngredientCard" key={`${item.id}-${index}`}>
                        {editingIngredientId === ingredient.id ? (
                          <div className="FoodDiaryEditBlock">
                            <div className="FoodDiaryTwoColumn">
                              <div className="inputDiv">
                                <label>Name</label>
                                <input
                                  className="inputBox addInput"
                                  value={ingredientForm.name}
                                  onChange={(event) => setIngredientForm((currentValue) => ({ ...currentValue, name: event.target.value }))}
                                />
                              </div>
                              <div className="inputDiv">
                                <label>Quantity Value</label>
                                <input
                                  className="inputBox addInput"
                                  value={ingredientForm.quantityValue}
                                  onChange={(event) => setIngredientForm((currentValue) => ({ ...currentValue, quantityValue: event.target.value }))}
                                />
                              </div>
                            </div>
                            <div className="FoodDiaryTwoColumn">
                              <div className="inputDiv">
                                <label>Quantity Text</label>
                                <input
                                  className="inputBox addInput"
                                  value={ingredientForm.quantityText}
                                  onChange={(event) => setIngredientForm((currentValue) => ({ ...currentValue, quantityText: event.target.value }))}
                                />
                              </div>
                              <div className="inputDiv">
                                <label>Unit</label>
                                <input
                                  className="inputBox addInput"
                                  value={ingredientForm.unit}
                                  onChange={(event) => setIngredientForm((currentValue) => ({ ...currentValue, unit: event.target.value }))}
                                />
                              </div>
                            </div>
                            <div className="inputDiv">
                              <label>Prep note</label>
                              <input
                                className="inputBox addInput"
                                value={ingredientForm.preparationNote}
                                onChange={(event) => setIngredientForm((currentValue) => ({ ...currentValue, preparationNote: event.target.value }))}
                              />
                            </div>
                            <div className="FoodDiaryActionRow">
                              <button className="summaryButton" onClick={() => saveIngredient(ingredient.id)}>Save</button>
                              <button className="summaryButton" onClick={() => setEditingIngredientId(null)}>Cancel</button>
                              <button className="summaryButton" onClick={() => deleteIngredient(ingredient.id)}>Delete Ingredient</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="FoodDiaryMealIngredient">
                              <span>{ingredient.name}</span>
                              <span>
                                {ingredient.quantityValue ?? ingredient.quantityText ?? ""}
                                {ingredient.unit ? ` ${ingredient.unit}` : ""}
                              </span>
                            </div>
                            {ingredient.preparationNote ? <div className="FoodDiaryMealNotes">{ingredient.preparationNote}</div> : null}
                            <div className="FoodDiaryActionRow">
                              <button className="summaryButton" onClick={() => beginIngredientEdit(ingredient)}>Edit Ingredient</button>
                              <button className="summaryButton" onClick={() => deleteIngredient(ingredient.id)}>Delete Ingredient</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FoodDiaryMeals;
